export const BACKUP_VERSION = 3;

const VALID_SORT_MODES = new Set(['priority', 'project', 'location']);
const NO_LOCATION_KEY = '__NONE__';

const createId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `id-${Math.random().toString(36).slice(2)}`;
};

const normalizeLocationKey = (key) => {
  const value = (key ?? '').trim();
  if (!value || value === 'No location' || value === NO_LOCATION_KEY) return NO_LOCATION_KEY;
  return value;
};

export const createBackupPayload = ({
  lists,
  locations,
  sortModeByList,
  locationOrderByList,
  projectTopOrderByList,
  projectChildOrderByList,
  projectCollapsedByList,
  lastView,
  recentlyDeleted
}) => ({
  _type: 'minimalist-backup',
  version: BACKUP_VERSION,
  exportedAt: new Date().toISOString(),
  data: {
    lists,
    locations,
    sortModeByList,
    locationOrderByList,
    projectTopOrderByList,
    projectChildOrderByList,
    projectCollapsedByList,
    lastView,
    recentlyDeleted
  }
});

export const normalizeRestoredLists = (rawLists, newId = createId) => {
  return (rawLists || []).map(list => {
    const tasks = Array.isArray(list.tasks) ? list.tasks.map(task => ({
      id: task.id || newId(),
      text: typeof task.text === 'string' ? task.text : '',
      done: Boolean(task.done),
      details: task.details && typeof task.details === 'object' ? { ...task.details } : {}
    })) : [];
    const taskIds = new Set(tasks.map(task => task.id));
    return {
      id: list.id || newId(),
      title: typeof list.title === 'string' ? list.title : 'Untitled',
      hideCompleted: Boolean(list.hideCompleted),
      tasks: tasks.map(task => {
        const parentId = task.details?.projectTaskId;
        if (!parentId || (taskIds.has(parentId) && parentId !== task.id)) return task;
        const { projectTaskId, ...details } = task.details || {};
        return { ...task, details };
      })
    };
  });
};

export const normalizeSortModeBackupState = (restoredLists, incomingSort) => {
  const source = incomingSort && typeof incomingSort === 'object' ? incomingSort : {};
  return restoredLists.reduce((acc, list) => {
    acc[list.id] = VALID_SORT_MODES.has(source[list.id]) ? source[list.id] : 'priority';
    return acc;
  }, {});
};

export const normalizeLocationOrderBackupState = (restoredLists, incomingOrder) => {
  const source = incomingOrder && typeof incomingOrder === 'object' ? incomingOrder : {};
  return restoredLists.reduce((acc, list) => {
    const taskIds = new Set((list.tasks || []).map(task => task.id));
    const byLocation = source[list.id] && typeof source[list.id] === 'object' ? source[list.id] : {};
    const normalizedByLocation = {};
    Object.entries(byLocation).forEach(([locationKey, ids]) => {
      if (!Array.isArray(ids)) return;
      const seen = new Set();
      const validIds = ids.filter(id => {
        if (!taskIds.has(id) || seen.has(id)) return false;
        seen.add(id);
        return true;
      });
      if (validIds.length) normalizedByLocation[normalizeLocationKey(locationKey)] = validIds;
    });
    acc[list.id] = normalizedByLocation;
    return acc;
  }, {});
};

export const normalizeProjectBackupState = (restoredLists, incomingTop, incomingChildren, incomingCollapsed) => {
  const topSource = incomingTop && typeof incomingTop === 'object' ? incomingTop : {};
  const childSource = incomingChildren && typeof incomingChildren === 'object' ? incomingChildren : {};
  const collapsedSource = incomingCollapsed && typeof incomingCollapsed === 'object' ? incomingCollapsed : {};
  const topByList = {};
  const childrenByList = {};
  const collapsedByList = {};

  restoredLists.forEach(list => {
    const taskIds = new Set((list.tasks || []).map(task => task.id));
    const childParentById = {};
    const fallbackTopOrder = [];
    const fallbackChildrenByParent = {};

    (list.tasks || []).forEach(task => {
      const parentId = task.details?.projectTaskId;
      if (parentId && parentId !== task.id && taskIds.has(parentId)) {
        childParentById[task.id] = parentId;
        fallbackChildrenByParent[parentId] = [...(fallbackChildrenByParent[parentId] || []), task.id];
      } else {
        fallbackTopOrder.push(task.id);
      }
    });

    const topSet = new Set(fallbackTopOrder);
    const seenTop = new Set();
    const incomingTopForList = Array.isArray(topSource[list.id]) ? topSource[list.id] : [];
    topByList[list.id] = [
      ...incomingTopForList.filter(id => {
        if (!topSet.has(id) || seenTop.has(id)) return false;
        seenTop.add(id);
        return true;
      }),
      ...fallbackTopOrder.filter(id => {
        if (seenTop.has(id)) return false;
        seenTop.add(id);
        return true;
      })
    ];

    const incomingChildrenForList = childSource[list.id] && typeof childSource[list.id] === 'object' ? childSource[list.id] : {};
    const allParentIds = new Set([
      ...Object.keys(fallbackChildrenByParent),
      ...Object.keys(incomingChildrenForList).filter(id => topSet.has(id))
    ]);
    const normalizedChildren = {};
    allParentIds.forEach(parentId => {
      if (!topSet.has(parentId)) return;
      const seenChildren = new Set();
      const incomingIds = Array.isArray(incomingChildrenForList[parentId]) ? incomingChildrenForList[parentId] : [];
      const orderedChildren = [
        ...incomingIds.filter(id => {
          if (childParentById[id] !== parentId || seenChildren.has(id)) return false;
          seenChildren.add(id);
          return true;
        }),
        ...(fallbackChildrenByParent[parentId] || []).filter(id => {
          if (seenChildren.has(id)) return false;
          seenChildren.add(id);
          return true;
        })
      ];
      if (orderedChildren.length) normalizedChildren[parentId] = orderedChildren;
    });
    childrenByList[list.id] = normalizedChildren;

    const incomingCollapsedForList = collapsedSource[list.id] && typeof collapsedSource[list.id] === 'object' ? collapsedSource[list.id] : {};
    collapsedByList[list.id] = Object.entries(incomingCollapsedForList).reduce((acc, [taskId, isCollapsed]) => {
      if (topSet.has(taskId)) acc[taskId] = Boolean(isCollapsed);
      return acc;
    }, {});
  });

  return { topByList, childrenByList, collapsedByList };
};
